import axios from "axios";
import React, { useState, useEffect } from "react";
import { Form, Button, Row, Col, Spinner, Alert } from "react-bootstrap";
import toast from "react-hot-toast";

const MyProfile = () => {
    // ⭐ 1. Function to get initial profile data from localStorage
    const getInitialProfile = () => {
        const storedProfile = localStorage.getItem('clientProfile');
        if (storedProfile) {
            try {
                const profileData = JSON.parse(storedProfile);
                return {
                    name: profileData.name || "",
                    mobile_no: profileData.mobile_no || "",
                    email: profileData.email || "",
                };
            } catch (e) {
                console.error("Failed to parse stored profile", e);
            }
        }
        return { name: "", mobile_no: "", email: "" };
    };

    // Initialize state using the data from localStorage
    const [profile, setProfile] = useState(getInitialProfile);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // --- 2. Fetch/Synchronize Profile Data on Load ---
    useEffect(() => {
        const token = window.localStorage.getItem("token");
        if (!token) {
            setError("Authentication token not found. Please log in.");
            setLoading(false);
            return;
        }

        const fetchProfile = async () => {
            // Assume loading is finished if local data is already present.
            // We set it to true initially, and rely on the fetch API to correctly set the final state.
            const isInitiallyLoaded = !!localStorage.getItem('clientProfile');
            
            // If data is in localStorage, the form is already pre-filled.
            // We proceed with fetching fresh data in the background.
            if (isInitiallyLoaded) {
                 setLoading(false);
            }
            
            try {
                // Fetch fresh data from the API
                const response = await axios.get('https://admin.tulikaarts.in/api/my-profile', {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                    },
                });

                // Assuming the API returns the user object directly or within a 'user' key
                const userData = response.data.user || response.data; 

                const updatedProfile = {
                    name: userData.name || "",
                    mobile_no: userData.mobile_no || "",
                    email: userData.email || "",
                };

                // Update state and refresh localStorage with the latest data
                setProfile(updatedProfile);
                localStorage.setItem('clientProfile', JSON.stringify(updatedProfile));
                
            } catch (err) {
                console.error("Error fetching fresh profile:", err);
                if (!isInitiallyLoaded) {
                     // Only show error if we failed to load from both local and API
                     setError("Failed to load profile data.");
                }
            } finally {
                setLoading(false);
            }
        };

        fetchProfile();
    }, []);

    // --- 3. Input Change Handler ---
    const handleChange = (e) => {
        const { name, value } = e.target;
        setProfile({
            ...profile,
            [name]: value,
        });
    };

    // --- 4. Form Submission Handler (Update Logic) ---
    const handleSubmit = (e) => {
        e.preventDefault();
        const id = toast.loading("Updating profile...");

        let config = {
            method: 'post',
            maxBodyLength: Infinity,
            url: 'https://admin.tulikaarts.in/api/update-profile',
            headers: {
                'Authorization': `Bearer ${window.localStorage.getItem("token")}`,
            },
            data: profile
        };

        axios.request(config)
            .then((response) => {
                toast.success(response.data.message || "Profile updated successfully!", { id });

                // ⭐ CRITICAL: Update localStorage with the new profile data
                localStorage.setItem('clientProfile', JSON.stringify(profile)); 
            })
            .catch((error) => {
                const responseData = error.response?.data;
                
                // Enhanced Error Handling for Laravel Validation (Status 422)
                if (error.response?.status === 422 && responseData?.errors) {
                    let errorMessages = Object.values(responseData.errors).flat();
                    errorMessages.forEach(msg => toast.error(msg, { id: 'temp' }));
                    toast.dismiss(id); 
                } else if (responseData?.message) {
                    // Handle specific API errors
                    toast.error(responseData.message, { id });
                } else {
                    // Handle generic network errors
                    toast.error("Profile update failed. Try again.", { id });
                }
            });
    };

    // --- Render Loading/Error States ---
    if (loading) {
        return (
            <div className='dashboard-pages-padding text-center' style={{ marginTop: "100px" }}>
                <Spinner animation="border" />
                <p>Loading Profile...</p>
            </div>
        );
    }

    if (error) {
        return (
            <div className='dashboard-pages-padding' style={{ marginTop: "100px" }}>
                <Alert variant="danger">{error}</Alert>
            </div>
        );
    }

    // --- Main Form Render ---
    return (
        <div className='dashboard-pages-padding' style={{ marginTop: "100px" }}>
            <Row className="justify-content-md-center">
                <Col md={6}>
                    <h1 className="mb-4">My Profile</h1>
                    <Form onSubmit={handleSubmit}>
                        
                        {/* Name */}
                        <Form.Group controlId="formName" className="mb-3">
                            <Form.Label>Name</Form.Label>
                            <Form.Control
                                type="text"
                                placeholder="Enter your name"
                                name="name"
                                required
                                value={profile.name}
                                onChange={handleChange}
                            />
                        </Form.Group>

                        {/* Mobile */}
                        <Form.Group controlId="formMobile" className="mb-3">
                            <Form.Label>Mobile</Form.Label>
                            <Form.Control
                                type="text"
                                required
                                placeholder="Enter your mobile number"
                                name="mobile_no"
                                value={profile.mobile_no}
                                onChange={handleChange}
                            />
                        </Form.Group>

                        {/* Email */}
                        <Form.Group controlId="formEmail" className="mb-4">
                            <Form.Label>Email</Form.Label>
                            <Form.Control
                                type="email"
                                placeholder="Enter your email"
                                name="email"
                                required
                                value={profile.email}
                                onChange={handleChange}
                            />
                        </Form.Group>

                        <Button variant="primary" type="submit" className="save-profile-btn w-100 py-2">
                            Save Profile
                        </Button>
                    </Form>
                </Col>
            </Row>
        </div>
    );
};

export default MyProfile;